import type * as THREE from 'three';
import {
  isFeatureResultMesh,
  type FeatureResultMesh,
} from '../../mesh/feature/FeatureResultMesh';
import { resolveModelBodySceneRegistry } from '../../model-body/model-body-scene-registry-runtime';

/**
 * 模型拓扑 owner 结果体解析输入。
 */
export interface ResolveModelTopologyOwnerResultMeshInput {
  /** 当前场景或导入实例根节点，用于回收按需创建的导入 pick host。 */
  readonly scene?: THREE.Object3D | null;
  /** 持久化或运行时记录的 owner objectId。 */
  readonly ownerObjectId: string | null | undefined;
  /** Viewer 提供的对象解析入口，优先保留既有手动结果体路径。 */
  readonly getSceneObjectById?: (objectId: string) => THREE.Object3D | null | undefined;
}

/**
 * 模型拓扑代理对象解析输入。
 */
export interface ResolveModelTopologyProxyObjectInput {
  /** 当前场景或导入实例根节点。 */
  readonly scene?: THREE.Object3D | null;
  /** 拓扑命中快照。 */
  readonly topologyPick: ResolvedTopologyPick;
  /** Viewer 提供的对象解析入口。 */
  readonly getSceneObjectById?: (objectId: string) => THREE.Object3D | null | undefined;
}

/**
 * 在场景中按 CAD objectId 查找结果体对象。
 *
 * @param root 当前场景或导入实例根节点。
 * @param objectId 待查找对象 ID。
 * @returns 命中的场景对象；未命中时返回 `null`。
 */
function findFeatureResultMeshByObjectId(
  root: THREE.Object3D | null | undefined,
  objectId: string,
): FeatureResultMesh | null {
  if (!root) {
    return null;
  }

  let target: FeatureResultMesh | null = null;
  root.traverse((object) => {
    const runtimeObjectId = (object as THREE.Object3D & { objectId?: string }).objectId;
    if (runtimeObjectId === objectId && isFeatureResultMesh(object)) {
      target = object;
    }
  });
  return target;
}

/**
 * 按统一 CAD body 语义解析 FeatureResultMesh。
 *
 * 设计说明：
 * 1. 所有实体先通过场景统一实体注册表解析，不区分导入或建模来源；
 * 2. 注册表返回的 topology owner 必须是 FeatureResultMesh，显示 Mesh 的实现类型不会泄漏到特征层；
 * 3. 直接场景命中只服务尚未接入注册表的非 Viewer 测试夹具和旧扩展对象。
 *
 * @param input 解析输入。
 * @returns 可供特征、草图、基准几何复用的 FeatureResultMesh；无法解析时返回 `null`。
 */
export function resolveModelTopologyOwnerResultMesh(
  input: ResolveModelTopologyOwnerResultMeshInput,
): FeatureResultMesh | null {
  const normalizedOwnerObjectId = input.ownerObjectId?.trim() ?? '';
  if (normalizedOwnerObjectId.length === 0) {
    return null;
  }

  const registeredOwner = input.scene
    ? resolveModelBodySceneRegistry(input.scene).resolveTopologyOwner(normalizedOwnerObjectId)
    : null;
  if (registeredOwner) {
    return registeredOwner;
  }

  const directObject = input.getSceneObjectById?.(normalizedOwnerObjectId)
    ?? findFeatureResultMeshByObjectId(input.scene, normalizedOwnerObjectId);
  if (directObject && isFeatureResultMesh(directObject)) {
    return directObject;
  }
  return null;
}

/**
 * 按拓扑命中快照解析当前运行时代理对象。
 *
 * @param input 解析输入。
 * @returns 当前结果体上的面、边或点代理；无法解析时返回 `null`。
 */
export function resolveModelTopologyProxyObject(
  input: ResolveModelTopologyProxyObjectInput,
): THREE.Object3D | null {
  const bodyObject = resolveModelTopologyOwnerResultMesh({
    scene: input.scene,
    ownerObjectId: input.topologyPick.bodyObjectId,
    getSceneObjectById: input.getSceneObjectById,
  });
  if (!bodyObject) {
    return null;
  }

  if (input.topologyPick.kind === 'face') {
    return bodyObject.getFeatureFaceByIndex(input.topologyPick.topologyIndex);
  }
  if (input.topologyPick.kind === 'edge') {
    return bodyObject.getFeatureLineByIndex(input.topologyPick.topologyIndex);
  }
  if (input.topologyPick.kind === 'vertex') {
    return bodyObject.getFeaturePointByIndex(input.topologyPick.topologyIndex);
  }
  return null;
}
